import { httpClient } from '../http/httpClient.js';
import { buildQueryString, ensureSuccessPayload, getCollection } from './serviceUtils.js';

const TASKS_API_URL = '/api/tasks';
const ASSIGNED_TASKS_API_URL = `${TASKS_API_URL}/assigned`;
const TASK_HISTORY_API_URL = `${TASKS_API_URL}/my-history`;

const buildTasksUrl = (params = {}) => {
  const queryString = buildQueryString(params);
  return queryString ? `${TASKS_API_URL}?${queryString}` : TASKS_API_URL;
};

const buildScopedUrl = (baseUrl, params = {}) => {
  const queryString = buildQueryString(params);
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

const isFormData = (value) => typeof FormData !== 'undefined' && value instanceof FormData;

const normalizeTaskDetail = (payload) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.task && typeof payload.task === 'object' && !Array.isArray(payload.task)) {
      return payload.task;
    }

    return payload;
  }

  throw new Error('Formato de tarea invalido');
};

const buildTaskCollectionResponse = (payload, collectionKeys = ['tasks', 'responses', 'data']) => {
  return {
    tasks: getCollection(payload, collectionKeys),
    pagination: payload?.pagination || null,
    payload
  };
};

const postTaskAction = async (taskId, action, { data, body } = {}, fallbackMessage) => {
  const requestOptions = {};

  if (body !== undefined) {
    requestOptions.body = body;
  } else if (data !== undefined) {
    requestOptions.data = data;
  }

  const payload = await httpClient.post(
    `${TASKS_API_URL}/${encodeURIComponent(taskId)}/${action}`,
    requestOptions
  );

  return ensureSuccessPayload(payload, fallbackMessage);
};

export const taskService = {
  getTasks: async (params = {}) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(buildTasksUrl(params)),
      'Error al cargar tareas'
    );

    return {
      tasks: getCollection(payload, ['responses', 'tasks', 'data']),
      pagination: payload?.pagination || null,
      payload
    };
  },

  getAssignedTasks: async ({ day = 'today' } = {}) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(buildScopedUrl(ASSIGNED_TASKS_API_URL, { day })),
      'No se pudieron cargar las tareas asignadas.'
    );

    return buildTaskCollectionResponse(payload);
  },

  getTaskDistributionPreview: async (distributionData) => {
    const payload = ensureSuccessPayload(
      await httpClient.post(`${TASKS_API_URL}/distribute`, { data: distributionData }),
      'No se pudo calcular la distribucion.'
    );

    return payload;
  },

  getCachedTaskDistributionPreview: async ({ date }) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(buildScopedUrl(`${TASKS_API_URL}/distribute/cache`, { date })),
      'No se pudo recuperar la distribucion cacheada.'
    );

    return payload?.preview || null;
  },

  discardCachedTaskDistributionPreview: async ({ date }) => {
    const payload = ensureSuccessPayload(
      await httpClient.delete(buildScopedUrl(`${TASKS_API_URL}/distribute/cache`, { date })),
      'No se pudo descartar la distribucion cacheada.'
    );

    return payload;
  },

  getConfirmedTaskDistribution: async ({ date, refreshOutdated = false } = {}) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(buildScopedUrl(`${TASKS_API_URL}/distribute/confirmed`, {
        date,
        refreshOutdated
      })),
      'No se pudieron recuperar los recorridos confirmados.'
    );

    return payload?.distribution || null;
  },

  confirmTaskDistribution: async (confirmationData) => {
    const payload = ensureSuccessPayload(
      await httpClient.post(`${TASKS_API_URL}/distribute/confirm`, { data: confirmationData }),
      'No se pudo confirmar la distribucion.'
    );

    return payload;
  },

  getMyHistory: async (params = {}) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(buildScopedUrl(TASK_HISTORY_API_URL, params)),
      'No se pudo cargar el historial de tareas.'
    );

    return buildTaskCollectionResponse(payload);
  },

  getTask: async (taskId) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(`${TASKS_API_URL}/${encodeURIComponent(taskId)}`),
      'No se pudo cargar la tarea.'
    );

    return normalizeTaskDetail(payload);
  },

  createTask: async (taskData) => {
    const payload = await httpClient.post(TASKS_API_URL, { data: taskData });
    return ensureSuccessPayload(payload, 'No se pudo crear la tarea.');
  },

  updateTask: async (taskId, taskData) => {
    const payload = await httpClient.put(`${TASKS_API_URL}/${encodeURIComponent(taskId)}`, {
      data: taskData
    });
    return ensureSuccessPayload(payload, 'No se pudo actualizar la tarea.');
  },

  deleteTask: async (taskId) => {
    const payload = await httpClient.delete(`${TASKS_API_URL}/${encodeURIComponent(taskId)}`);
    return ensureSuccessPayload(payload, 'No se pudo eliminar la tarea.');
  },

  getCancelledTasks: async (params = {}) => {
    const queryString = buildQueryString(params);
    const url = queryString ? `${TASKS_API_URL}/cancelled?${queryString}` : `${TASKS_API_URL}/cancelled`;
    const payload = ensureSuccessPayload(
      await httpClient.get(url),
      'No se pudieron obtener las tareas canceladas.'
    );

    return getCollection(payload, ['tasks', 'responses', 'data']);
  },

  reactivateTask: async (taskId, reactivationData) => {
    const payload = await httpClient.post(`${TASKS_API_URL}/${encodeURIComponent(taskId)}/reactivate`, {
      data: reactivationData
    });
    return ensureSuccessPayload(payload, 'No se pudo reactivar la tarea.');
  },

  bulkAssignTeam: async ({ teamId, taskIds, forceCreate = false }) => {
    const payload = await httpClient.post(`${TASKS_API_URL}/bulk-assign-team`, {
      data: { teamId, taskIds, forceCreate }
    });
    return ensureSuccessPayload(payload, 'No se pudieron reasignar las tareas.');
  },

  reassignTaskTeam: async (taskId, teamId, forceCreate = false) => {
    const payload = await httpClient.put(`${TASKS_API_URL}/${encodeURIComponent(taskId)}/reassign-team`, {
      data: { teamId, forceCreate }
    });
    return ensureSuccessPayload(payload, 'No se pudo reasignar el equipo.');
  },

  exportTasksCsv: async (params = {}) => {
    const queryString = buildQueryString(params);
    const url = queryString ? `${TASKS_API_URL}/export?${queryString}` : `${TASKS_API_URL}/export`;
    return httpClient.get(url, { parseAs: 'blob' });
  },

  validateTask: async (taskId, validationData) => {
    return postTaskAction(
      taskId,
      'validate',
      { data: validationData },
      'No se pudo validar la tarea.'
    );
  },

  finishTask: async (taskId) => {
    return postTaskAction(taskId, 'finish', {}, 'No se pudo finalizar la tarea.');
  },

  startTask: async (taskId) => {
    return postTaskAction(taskId, 'start', {}, 'No se pudo iniciar la tarea.');
  },

  updateTaskStatus: async (taskId, action) => {
    return postTaskAction(
      taskId,
      action,
      {},
      'No se pudo actualizar el estado de la tarea.'
    );
  },

  cancelTask: async (taskId, cancelData) => {
    return cancelData === undefined
      ? postTaskAction(taskId, 'cancel', {}, 'No se pudo cancelar la tarea.')
      : postTaskAction(
        taskId,
        'cancel',
        { data: cancelData },
        'No se pudo cancelar la tarea.'
      );
  },

  completeTaskForm: async (taskId, formData) => {
    const payload = await httpClient.post(
      `${TASKS_API_URL}/${encodeURIComponent(taskId)}/form-completed`,
      isFormData(formData)
        ? { body: formData }
        : { data: formData }
    );
    return ensureSuccessPayload(payload, 'No se pudo enviar el formulario.');
  },

  checkFormCompletion: async (taskId) => {
    const payload = await httpClient.get(
      `${TASKS_API_URL}/check-form-completion/${encodeURIComponent(taskId)}`
    );

    return ensureSuccessPayload(payload, 'No se pudo verificar el formulario.');
  },

  getTaskFormsResponses: async (taskId) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(`${TASKS_API_URL}/forms-responses?${buildQueryString({ taskId })}`),
      'No se pudieron cargar los formularios de la tarea.'
    );

    return getCollection(payload, ['responses', 'data']);
  },

  getTaskActivityHistory: async (taskId) => {
    const payload = ensureSuccessPayload(
      await httpClient.get(`${TASKS_API_URL}/${encodeURIComponent(taskId)}/activity-history`),
      'No se pudo cargar el historial de actividad de la tarea.'
    );

    return getCollection(payload, ['history', 'items', 'data']);
  }
};
